import React, { useState } from 'react'
import { X, Crown, Check } from 'lucide-react'
import { useAuth } from '../hooks/useAuth.jsx'
import { sage, neutral, gold, fonts, shadows } from '../styles/theme.js'

const API_URL = import.meta.env.VITE_API_URL || ''

const FEATURES = [
  { text: 'Unlimited recipe access',      sub: true,  free: false },
  { text: 'Import from URL',              sub: true,  free: false },
  { text: 'Import from screenshot',       sub: true,  free: false },
  { text: 'Pinterest import',             sub: true,  free: false },
  { text: 'AI cooking assistant',         sub: true,  free: false },
  { text: 'Personal recipe notes',        sub: true,  free: false },
  { text: 'First recipe only',            sub: false, free: true  },
  { text: 'Unit conversions',             sub: true,  free: true  },
  { text: 'Cooking timers',               sub: true,  free: true  },
]

export default function UpgradeModal({ recipeTitle, onClose }) {
  const { authFetch } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubscribe = async () => {
    setLoading(true); setError('')
    try {
      const res = await authFetch('/api/billing/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/recipes`,
          cancelUrl: window.location.href,
        }),
      })
      if (!res.ok) throw new Error('Failed to create checkout session')
      const { url } = await res.json()
      window.location.href = url
    } catch (e) {
      setError(e.message || 'Something went wrong')
    } finally { setLoading(false) }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)',
      zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, animation: 'fadeIn 0.2s ease', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 460, background: '#ffffff', borderRadius: 16,
        overflow: 'hidden', boxShadow: shadows.modal, animation: 'slideIn 0.25s ease',
      }}>
        {/* Header */}
        <div style={{
          background: sage.dark, padding: '28px 32px', position: 'relative',
        }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6 }}>
            <X size={20} color="white" />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Crown size={22} color={gold.light} />
            <span style={{ fontFamily: fonts.serif, fontSize: 13, fontWeight: 600, color: gold.light, letterSpacing: 1, textTransform: 'uppercase' }}>
              Upgrade
            </span>
          </div>
          <h2 style={{ fontFamily: fonts.cursive, fontSize: 32, fontWeight: 700, color: 'white', marginBottom: 6 }}>
            My Cookbook Pro
          </h2>
          {recipeTitle && (
            <p style={{ fontFamily: fonts.serif, fontSize: 15, color: `${sage.pale}cc` }}>
              Unlock "<em>{recipeTitle}</em>" and everything else
            </p>
          )}
          <div style={{ marginTop: 16, display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontFamily: fonts.cursive, fontSize: 40, fontWeight: 700, color: 'white' }}>$4.99</span>
            <span style={{ fontFamily: fonts.serif, fontSize: 15, color: `${sage.pale}99` }}>/month</span>
          </div>
        </div>

        {/* Feature list */}
        <div style={{ padding: '24px 32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 20px', alignItems: 'center', marginBottom: 8 }}>
            <span />
            <span style={{ fontFamily: fonts.serif, fontSize: 12, color: neutral[500], textAlign: 'center', fontWeight: 600 }}>FREE</span>
            <span style={{ fontFamily: fonts.serif, fontSize: 12, color: sage.mid, textAlign: 'center', fontWeight: 600 }}>PRO</span>
          </div>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 20px',
              alignItems: 'center', padding: '7px 0',
              borderBottom: i < FEATURES.length - 1 ? `1px solid ${neutral[100]}` : 'none',
            }}>
              <span style={{ fontFamily: fonts.serif, fontSize: 15, color: neutral[700] }}>{f.text}</span>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {f.free ? <Check size={15} color="#27ae60" /> : <X size={13} color={neutral[300]} />}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {f.sub ? <Check size={15} color={sage.mid} /> : <X size={13} color={neutral[300]} />}
              </div>
            </div>
          ))}

          {error && <p style={{ fontFamily: fonts.serif, fontSize: 14, color: '#c0654a', marginTop: 12 }}>{error}</p>}

          <button
            onClick={handleSubscribe}
            disabled={loading}
            style={{
              width: '100%', marginTop: 20, padding: '14px',
              background: loading ? neutral[300] : sage.dark,
              border: 'none', borderRadius: 10, color: 'white',
              fontFamily: fonts.serif, fontSize: 16, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: `0 4px 16px ${sage.dark}30`,
              transition: 'background 0.2s',
            }}
          >
            {loading ? 'Redirecting to Stripe…' : 'Subscribe — $4.99/month'}
          </button>
          <p style={{ textAlign: 'center', fontFamily: fonts.serif, fontSize: 13, color: neutral[500], marginTop: 10 }}>
            Cancel anytime · Secure payment via Stripe
          </p>
        </div>
      </div>
    </div>
  )
}
